import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const CYAN = "#81ecff";

export default function Fans() {
  const { active } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["fans", active?.slug],
    queryFn: () => endpoints.fans(active!.slug),
    enabled: !!active,
  });

  if (isLoading) return <div className="p-8"><Spinner /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>Fans</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {data.total.toLocaleString()} identified · captured via opt-in on public smartlinks
          </p>
        </div>
        <Link to="/app/fans/segments">
          <Button variant="outline">AI segments →</Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-foreground uppercase mb-4" style={{ fontSize: "14px", letterSpacing: "0.02em" }}>Geography</h2>
            {data.byCountry.length === 0 ? (
              <p className="text-sm text-muted-foreground">No fans yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead className="text-right">Fans</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.byCountry.map((c) => (
                    <TableRow key={c.country}>
                      <TableCell><span style={{ color: "#adaaaa" }}>{c.country}</span></TableCell>
                      <TableCell className="text-right tabular-nums font-normal">{c.n}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="pt-6">
            <div className="flex items-end justify-between mb-4">
              <div>
                <h2 className="text-foreground uppercase" style={{ fontSize: "14px", letterSpacing: "0.02em" }}>Recent (50)</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">Last 50 identifications by activity</p>
              </div>
            </div>
            {data.recent.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-12">
                No fans yet. They show up once visitors opt in on your smart links.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>First seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell className="font-normal">
                        <Link to={`/app/fans/${f.id}`} className="hover:underline">
                          {f.email ?? "(anonymous)"}
                        </Link>
                      </TableCell>
                      <TableCell><span style={{ color: "#adaaaa" }}>{f.country ?? "—"}</span></TableCell>
                      <TableCell><span style={{ color: "#adaaaa" }}>{f.source ?? "—"}</span></TableCell>
                      <TableCell>
                        {f.first_seen ? (
                          <span style={{ backgroundColor: "#262626", color: CYAN, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
                            {f.first_seen.slice(0, 10)}
                          </span>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
